import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useConfirm } from "../components/useConfirm";
import { ROLE_LABELS, ROLE_COLORS, ASSIGNABLE_ROLES } from "../lib/roles";
import { useAuth } from "../context/AuthContext";
import { Users as UsersIcon, Trash2, ShieldCheck, Clock, Ticket, Copy, Plus, KeyRound } from "lucide-react";

const INVITE_ROLE_OPTIONS = ["wellness", "ai", "book"];

export function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [newRole, setNewRole] = useState("wellness");
  const [newExpiry, setNewExpiry] = useState("0");
  const [newMaxUses, setNewMaxUses] = useState("0");
  const { confirm, ConfirmUI } = useConfirm();

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data));
  const loadInvites = () => api.get("/invites").then((r) => setInvites(r.data));
  useEffect(() => { load(); loadInvites(); }, []);

  const inviteLink = (token) => `${window.location.origin}/?invite=${token}`;

  const createInvite = async () => {
    await api.post("/invites", { role: newRole, expires_hours: Number(newExpiry), max_uses: Number(newMaxUses) });
    toast.success("Link d'invito generato");
    loadInvites();
  };

  const copyInvite = (token) => {
    navigator.clipboard.writeText(inviteLink(token));
    toast.success("Link d'invito copiato! Condividilo con il collaboratore.");
  };

  const removeInvite = async (inv) => {
    const ok = await confirm({
      title: "Revocare l'invito?",
      description: "Il link non funzionerà più. Chi è già registrato non sarà toccato.",
      confirmLabel: "Revoca", variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/invites/${inv.id}`);
    toast.success("Invito revocato");
    loadInvites();
  };

  const changeRole = async (u, role) => {
    await api.put(`/auth/users/${u.id}`, { role, approved: role !== "pending" });
    toast.success(`Ruolo di ${u.name} aggiornato: ${ROLE_LABELS[role]}`);
    load();
  };

  const remove = async (u) => {
    const ok = await confirm({
      title: "Eliminare l'utente?",
      description: `Vuoi eliminare l'account di ${u.name} (${u.email})? L'operazione non può essere annullata.`,
      confirmLabel: "Elimina",
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/auth/users/${u.id}`);
    toast.success("Utente eliminato");
    load();
  };

  const resetPassword = async (u) => {
    const ok = await confirm({
      title: "Reimpostare la password?",
      description: `Verrà generata una password provvisoria per ${u.name} e inviata via email a ${u.email}. Se l'email non arriva, potrai comunicarla tu.`,
      confirmLabel: "Reimposta",
    });
    if (!ok) return;
    const { data } = await api.post(`/auth/users/${u.id}/reset-password`);
    if (data.emailed) {
      toast.success(`Password provvisoria inviata via email a ${u.email}`);
    } else {
      await confirm({
        title: "Password provvisoria generata",
        description: `Email non inviata automaticamente. Comunica tu questa password provvisoria a ${u.name}:\n\n${data.temp_password}`,
        confirmLabel: "Ho copiato",
      });
      navigator.clipboard.writeText(data.temp_password).catch(() => {});
    }
  };

  const pending = users.filter((u) => u.role === "pending");

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="users-view">
      {pending.length > 0 && (
        <div className="glass rounded-2xl p-4 border border-amber-500/30 bg-amber-500/5 flex items-center gap-3">
          <Clock className="h-5 w-5 text-amber-400 shrink-0" />
          <p className="text-sm text-amber-100/90">
            <strong>{pending.length}</strong> {pending.length === 1 ? "nuovo utente è" : "nuovi utenti sono"} in attesa di approvazione. Assegna un ruolo per sbloccare l'accesso.
          </p>
        </div>
      )}

      {/* Inviti collaboratori */}
      <div className="glass rounded-2xl p-5" data-testid="invites-panel">
        <div className="flex items-center gap-2 mb-2">
          <Ticket className="h-5 w-5 text-emerald-400" />
          <h3 className="font-display font-bold">Invita collaboratori</h3>
        </div>
        <p className="text-sm text-slate-400 mb-4">Genera un link con ruolo già assegnato: chi si registra da quel link entra <strong>subito operativo</strong>, senza attendere la tua approvazione.</p>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select data-testid="invite-role-select" value={newRole} onChange={(e) => setNewRole(e.target.value)}
            className="bg-[#14161d] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-400/50">
            {INVITE_ROLE_OPTIONS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <select data-testid="invite-expiry-select" value={newExpiry} onChange={(e) => setNewExpiry(e.target.value)}
            className="bg-[#14161d] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-400/50">
            <option value="0">Nessuna scadenza</option>
            <option value="24">Scade in 24 ore</option>
            <option value="72">Scade in 3 giorni</option>
            <option value="168">Scade in 7 giorni</option>
          </select>
          <select data-testid="invite-maxuses-select" value={newMaxUses} onChange={(e) => setNewMaxUses(e.target.value)}
            className="bg-[#14161d] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-400/50">
            <option value="0">Usi illimitati</option>
            <option value="1">Monouso</option>
            <option value="5">Max 5 usi</option>
            <option value="10">Max 10 usi</option>
          </select>
          <button data-testid="create-invite-button" onClick={createInvite}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-400 transition-colors">
            <Plus className="h-4 w-4" /> Genera link d'invito
          </button>
        </div>
        {invites.length === 0 ? (
          <p className="text-sm text-slate-500">Nessun invito attivo.</p>
        ) : (
          <div className="space-y-2">
            {invites.map((inv) => (
              <div key={inv.id} data-testid={`invite-${inv.id}`} className="flex items-center gap-2 p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
                <span className="text-xs font-semibold px-2 py-1 rounded-full shrink-0"
                  style={{ background: `${ROLE_COLORS[inv.role]}22`, color: ROLE_COLORS[inv.role] }}>{ROLE_LABELS[inv.role]}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${inv.status === "attivo" ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>{inv.status}</span>
                <code className="text-[11px] text-slate-400 truncate flex-1">{inviteLink(inv.token)}</code>
                <span className="text-[10px] text-slate-500 shrink-0 hidden sm:inline">
                  {inv.max_uses ? `${inv.uses}/${inv.max_uses} usi` : `${inv.uses} usi`}
                  {inv.expires_at ? " · scad." : ""}
                </span>
                <button data-testid={`copy-invite-${inv.id}`} onClick={() => copyInvite(inv.token)} className="p-2 rounded-lg hover:bg-emerald-500/20 shrink-0"><Copy className="h-4 w-4 text-emerald-300" /></button>
                <button data-testid={`delete-invite-${inv.id}`} onClick={() => removeInvite(inv)} className="p-2 rounded-lg hover:bg-rose-500/20 shrink-0"><Trash2 className="h-4 w-4 text-rose-400" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-5">
          <UsersIcon className="h-5 w-5 text-slate-300" />
          <h3 className="font-display font-bold">Gestione utenti e ruoli</h3>
          <span className="ml-auto text-xs text-slate-400">{users.length} utenti</span>
        </div>

        <div className="space-y-3">
          {users.map((u, i) => {
            const isMainAdmin = u.email === (me?.email || "") && u.role === "admin";
            return (
              <motion.div key={u.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5" data-testid={`user-row-${u.id}`}>
                <div className="h-10 w-10 rounded-full grid place-items-center font-display font-bold text-sm shrink-0"
                  style={{ background: `${ROLE_COLORS[u.role]}22`, color: ROLE_COLORS[u.role] }}>
                  {(u.name || u.email)[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{u.name} {isMainAdmin && <span className="text-[10px] text-indigo-300">(tu)</span>}</p>
                  <p className="text-xs text-slate-400 truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  {u.role !== "pending" && <ShieldCheck className="h-4 w-4" style={{ color: ROLE_COLORS[u.role] }} />}
                  <select
                    data-testid={`user-role-${u.id}`}
                    value={u.role}
                    disabled={isMainAdmin}
                    onChange={(e) => changeRole(u, e.target.value)}
                    className="bg-[#14161d] border border-white/10 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:border-indigo-400/50 disabled:opacity-50">
                    {ASSIGNABLE_ROLES.map((r) => (
                      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                  <button data-testid={`reset-user-${u.id}`} onClick={() => resetPassword(u)} title="Reimposta password"
                    className="p-2 rounded-lg hover:bg-amber-500/20">
                    <KeyRound className="h-4 w-4 text-amber-300" />
                  </button>
                  <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} disabled={isMainAdmin}
                    className="p-2 rounded-lg hover:bg-rose-500/20 disabled:opacity-30">
                    <Trash2 className="h-4 w-4 text-rose-400" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
      {ConfirmUI}
    </div>
  );
}
